import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const History = () => {
  const [activeTab, setActiveTab] = useState('results'); // 'results' or 'quizzes'
  const [history, setHistory] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState(null);
  const [embedModal, setEmbedModal] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const { isPremium, isFree } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch if premium user
    if (isPremium) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchData = async () => {
    try {
      // Fetch both history and created quizzes
      const [historyRes, quizzesRes] = await Promise.all([
        axios.get(`${API_URL}/api/quiz/history/me?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/quiz/my-quizzes?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setHistory(historyRes.data);
      setQuizzes(quizzesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // If 403 error, user is on free tier
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show upgrade modal for free tier users
  if (isFree) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <UpgradeModal 
          isOpen={true} 
          onClose={() => navigate('/dashboard')}
          feature="history"
        />
        
        <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <button onClick={() => navigate('/dashboard')} className={`flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto py-16 px-4 text-center">
          <div className="text-6xl mb-6">🔒</div>
          <h1 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Quiz History & Analytics
          </h1>
          <p className={`text-lg mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Track your progress, review past quizzes, and analyze your performance with Premium.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-purple-700"
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportPDF = async (quiz, includeAnswers) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/export/pdf/${quiz.quiz_id}?include_answers=${includeAnswers}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quiz.title.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const handleShare = async (quiz) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/export/share/${quiz.quiz_id}`,
        { access_mode: 'view' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const shareUrl = `${window.location.origin}${response.data.share_url}`;
      setShareModal({ quiz, shareUrl, token: response.data.share_token });
    } catch (error) {
      console.error('Failed to create share link:', error);
      alert('Failed to create share link. Please try again.');
    }
  };

  const handleEmbed = async (quiz) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/export/embed/${quiz.quiz_id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setEmbedModal({ quiz, embedCode: response.data.embed_code, shareUrl: response.data.share_url });
    } catch (error) {
      console.error('Failed to generate embed code:', error);
      alert('Failed to generate embed code. Please try again.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleCopyText = (quiz) => {
    try {
      console.log('Starting copy process for quiz:', quiz);
      
      // Check if quiz has questions
      if (!quiz.questions || quiz.questions.length === 0) {
        alert('⚠️ This quiz has no questions to copy.');
        return;
      }
      
      // Generate formatted text directly from quiz object
      let text = `${quiz.title}\n`;
      text += `${'='.repeat(quiz.title.length)}\n\n`;
      text += `Difficulty: ${quiz.difficulty}\n`;
      text += `Total Questions: ${quiz.questions.length}\n\n`;
      
      // Add questions
      quiz.questions.forEach((question, idx) => {
        text += `${idx + 1}. ${question.question}\n`;
        
        if (question.options && question.options.length > 0) {
          question.options.forEach((option, optIdx) => {
            const label = String.fromCharCode(65 + optIdx); // A, B, C, D
            text += `   ${label}) ${option}\n`;
          });
        } else {
          text += `   Answer: _______________________________\n`;
        }
        text += `\n`;
      });
      
      // Add answer key
      text += `\n${'='.repeat(20)}\n`;
      text += `ANSWER KEY\n`;
      text += `${'='.repeat(20)}\n\n`;
      
      quiz.questions.forEach((question, idx) => {
        text += `${idx + 1}. ${question.correct_answer}\n`;
        if (question.explanation) {
          text += `   Explanation: ${question.explanation}\n`;
        }
        text += `\n`;
      });
      
      console.log('Generated text (first 200 chars):', text.substring(0, 200));
      
      // Copy to clipboard using fallback method (more reliable)
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          console.log('Copy successful!');
          alert('✅ Quiz copied to clipboard!\n\nOptimized for WhatsApp, Google Classroom, and email.');
        } else {
          console.log('Copy command failed');
          alert('⚠️ Failed to copy. Please try again or use Ctrl+C manually.');
        }
      } catch (err) {
        console.error('Copy error:', err);
        alert('⚠️ Copy failed: ' + err.message);
      } finally {
        document.body.removeChild(textArea);
      }
      
    } catch (error) {
      console.error('Copy text error:', error);
      alert('❌ Error: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Quiz Management 📊</h1>
          <p className="text-lg text-gray-600">Track your progress, manage, and share your quizzes</p>
        </div>

        {/* Tabs */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('results')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'results'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Quiz Results ({history.length})
            </button>
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'quizzes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Quizzes ({quizzes.length})
            </button>
          </nav>
        </div>

        {/* Results Tab */}
        {activeTab === 'results' && history.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Quiz History Yet</h3>
            <p className="text-gray-600 mb-6">Start taking quizzes to see your progress here</p>
            <button
              onClick={() => navigate('/quiz/generate')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Generate Your First Quiz
            </button>
          </div>
        ) : activeTab === 'results' ? (
          <div className="space-y-4">
            {history.map((result, idx) => {
              const passed = result.percentage >= 70;
              
              return (
                <div
                  key={idx}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/quiz/result/${result.quiz_id}`, { state: { answers: result.answers } })}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-3">{passed ? '🏆' : '💪'}</span>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            Quiz from {formatDate(result.completed_at)}
                          </h3>
                          <p className="text-sm text-gray-500">ID: {result.quiz_id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${
                          passed ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {result.percentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          {result.score}/{result.total}
                        </div>
                      </div>
                      
                      <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                        passed 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {passed ? 'Passed' : 'Review'}
                      </div>
                      
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Quizzes Tab */}
        {activeTab === 'quizzes' && quizzes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Quizzes Created Yet</h3>
            <p className="text-gray-600 mb-6">Create your first quiz to see it here</p>
            <button
              onClick={() => navigate('/quiz/generate')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
            >
              Generate Your First Quiz
            </button>
          </div>
        ) : activeTab === 'quizzes' ? (
          <div className="space-y-4">
            {quizzes.map((quiz, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {quiz.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="capitalize">📊 {quiz.difficulty}</span>
                      <span>❓ {quiz.questions.length} questions</span>
                      <span>📅 {formatDate(quiz.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Export PDF Dropdown */}
                    <div className="relative group">
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Export PDF</span>
                      </button>
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                        <button
                          onClick={() => handleExportPDF(quiz, false)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-gray-700 first:rounded-t-lg"
                        >
                          📄 Questions Only
                        </button>
                        <button
                          onClick={() => handleExportPDF(quiz, true)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-gray-700 last:rounded-b-lg border-t"
                        >
                          ✅ Questions with Answers
                        </button>
                      </div>
                    </div>

                    {/* Share Button */}
                    <button
                      onClick={() => handleShare(quiz)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                      <span>Share</span>
                    </button>

                    {/* Embed Button */}
                    <button
                      onClick={() => handleEmbed(quiz)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      <span>Embed</span>
                    </button>

                    {/* Copy Text Button - NEW FEATURE */}
                    <button
                      onClick={() => handleCopyText(quiz)}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all flex items-center space-x-2 relative group"
                      title="Copy as text"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy Text</span>
                      <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap">
                        Optimized for classroom posts and chats
                      </div>
                    </button>

                    {/* View Quiz */}
                    <button
                      onClick={() => navigate(`/quiz/take/${quiz.quiz_id}`)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4">Share Quiz: {shareModal.quiz.title}</h2>
            <p className="text-gray-600 mb-4">Anyone with this link can view this quiz (read-only mode)</p>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-2">Share URL:</p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareModal.shareUrl}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white"
                />
                <button
                  onClick={() => copyToClipboard(shareModal.shareUrl)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Copy
                </button>
              </div>
            </div>

            <button
              onClick={() => setShareModal(null)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Embed Modal */}
      {embedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setEmbedModal(null)}>
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4">Embed Quiz: {embedModal.quiz.title}</h2>
            <p className="text-gray-600 mb-4">Use this HTML code to embed the quiz in your LMS or website</p>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-2">Embed Code:</p>
              <textarea
                value={embedModal.embedCode}
                readOnly
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(embedModal.embedCode)}
                className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Copy Embed Code
              </button>
            </div>

            <button
              onClick={() => setEmbedModal(null)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
